import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { GroceryListDetail, GroceryListDto } from '@fewerbags/schema';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { groceryKeys } from '../../pages/groceries/queries';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { Field } from '../Field';

/**
 * Re-run generation for this list from a (possibly new) date range. Manual
 * items and anything already checked off are kept — the API layers the fresh
 * plan-derived lines on top rather than replacing the list wholesale.
 */
export function RegenerateListDialog({
  open,
  onClose,
  list,
}: {
  open: boolean;
  onClose: () => void;
  list: GroceryListDto;
}) {
  const qc = useQueryClient();
  const [dateFrom, setDateFrom] = useState(list.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(list.dateTo ?? '');
  const [subtractPantry, setSubtractPantry] = useState(true);
  const [includeRestock, setIncludeRestock] = useState(true);

  const regenerate = useMutation({
    mutationFn: () =>
      api.groceries.regenerate(list.id, {
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
        subtractPantry,
        includeRestock,
      }),
    onSuccess: async (detail: GroceryListDetail) => {
      qc.setQueryData(groceryKeys.detail(list.id), detail);
      await qc.invalidateQueries({ queryKey: groceryKeys.all });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} title="Regenerate this list">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">
          Re-reads your planned meals for these dates. Items you added yourself and anything already
          checked off stay on the list.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field
            id="regen-from"
            label="From"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <Field
            id="regen-to"
            label="To"
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={subtractPantry}
            onChange={(e) => setSubtractPantry(e.target.checked)}
            className="size-5 accent-brand"
          />
          Subtract what's already in the pantry
        </label>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={includeRestock}
            onChange={(e) => setIncludeRestock(e.target.checked)}
            className="size-5 accent-brand"
          />
          Add pantry staples that are running low
        </label>

        {regenerate.isError ? (
          <Alert tone="error">{messageForError(regenerate.error)}</Alert>
        ) : null}

        <div className="flex gap-2">
          <Button
            onClick={() => regenerate.mutate()}
            disabled={regenerate.isPending || (!!dateFrom && !!dateTo && dateTo < dateFrom)}
          >
            {regenerate.isPending ? 'Regenerating…' : 'Regenerate'}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={regenerate.isPending}>
            Cancel
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
